import type { TopzleGameDefinition } from "@topzle/game-sdk";
import type { GameContext, GameResult, ViewerContext } from "@topzle/types";
import { z } from "zod";

export const mafiaRoles = ["mafia", "detective", "doctor", "citizen"] as const;
const mafiaRoleSchema = z.enum(mafiaRoles);
const mafiaPhaseSchema = z.enum([
  "role_reveal",
  "night",
  "dawn",
  "discussion",
  "voting",
  "finished",
]);
const mafiaWinnerSchema = z.enum(["town", "mafia"]);

const investigationSchema = z.object({
  day: z.number().int().positive(),
  targetId: z.string(),
  isMafia: z.boolean(),
});
const historyEntrySchema = z.object({
  day: z.number().int().positive(),
  kind: z.enum(["night", "vote"]),
  eliminatedId: z.string().nullable(),
  saved: z.boolean().default(false),
  tied: z.boolean().default(false),
});

export const mafiaStateSchema = z.object({
  players: z.array(z.string()).min(1).max(12),
  roles: z.record(z.string(), mafiaRoleSchema),
  aliveIds: z.array(z.string()).min(1).max(12),
  phase: mafiaPhaseSchema,
  day: z.number().int().positive(),
  roleAcknowledgedIds: z.array(z.string()).max(12),
  mafiaVotes: z.record(z.string(), z.string()),
  doctorTargetId: z.string().nullable(),
  detectiveTargetId: z.string().nullable(),
  lastProtectedId: z.string().nullable(),
  investigations: z.record(z.string(), z.array(investigationSchema).max(20)),
  readyToVoteIds: z.array(z.string()).max(12),
  votes: z.record(z.string(), z.string().nullable()),
  nightVictimId: z.string().nullable(),
  nightSaved: z.boolean(),
  lastEliminatedId: z.string().nullable(),
  history: z.array(historyEntrySchema).max(40),
  deadlineAt: z.number().int().nonnegative().nullable(),
  winner: mafiaWinnerSchema.nullable(),
  revealRolesOnDeath: z.boolean(),
  discussionSeconds: z.number().int().min(30).max(300),
  votingSeconds: z.number().int().min(20).max(120),
  nightSeconds: z.number().int().min(20).max(120),
  scores: z.record(z.string(), z.number().int().min(0).max(1)),
});

export const mafiaEventSchema = z.discriminatedUnion("type", [
  z.object({ type: z.literal("acknowledge_role") }),
  z.object({ type: z.literal("advance_phase") }),
  z.object({
    type: z.literal("night_action"),
    targetId: z.string(),
  }),
  z.object({ type: z.literal("ready_to_vote") }),
  z.object({
    type: z.literal("cast_vote"),
    targetId: z.string().nullable(),
  }),
]);

export const mafiaSettingsSchema = z.object({
  discussionSeconds: z.number().int().min(30).max(300).default(90),
  votingSeconds: z.number().int().min(20).max(120).default(45),
  nightSeconds: z.number().int().min(20).max(120).default(40),
  revealRolesOnDeath: z.boolean().default(true),
});

export type MafiaRole = z.infer<typeof mafiaRoleSchema>;
export type MafiaPhase = z.infer<typeof mafiaPhaseSchema>;
export type MafiaState = z.infer<typeof mafiaStateSchema>;
export type MafiaEvent = z.infer<typeof mafiaEventSchema>;
export type MafiaSettings = z.infer<typeof mafiaSettingsSchema>;

export interface MafiaPublicState {
  players: string[];
  aliveIds: string[];
  phase: MafiaPhase;
  day: number;
  roleAcknowledgedIds: string[];
  readyToVoteIds: string[];
  votes: Record<string, string | null>;
  nightVictimId: string | null;
  nightSaved: boolean;
  lastEliminatedId: string | null;
  history: MafiaState["history"];
  deadlineAt: number | null;
  winner: "town" | "mafia" | null;
  scores: Record<string, number>;
  ownRole: MafiaRole | null;
  mafiaTeamIds: string[];
  revealedRoles: Record<string, MafiaRole>;
  ownNightTargetId: string | null;
  mafiaNightVotes: Record<string, string>;
  investigations: Array<z.infer<typeof investigationSchema>>;
}

export function mafiaRoleCounts(playerCount: number): Record<MafiaRole, number> {
  const mafia = playerCount >= 9 ? 3 : playerCount >= 6 ? 2 : 1;
  return {
    mafia,
    detective: 1,
    doctor: 1,
    citizen: Math.max(1, playerCount - mafia - 2),
  };
}

function shuffled<T>(values: T[], context: GameContext): T[] {
  const result = [...values];
  for (let index = result.length - 1; index > 0; index -= 1) {
    const picked = Math.abs(context.randomInt?.(index + 1) ?? index) % (index + 1);
    [result[index], result[picked]] = [result[picked] as T, result[index] as T];
  }
  return result;
}

function livingWithRole(state: MafiaState, role: MafiaRole): string[] {
  return state.aliveIds.filter((playerId) => state.roles[playerId] === role);
}

function winningFaction(state: MafiaState): "town" | "mafia" | null {
  const livingMafia = livingWithRole(state, "mafia").length;
  const livingTown = state.aliveIds.length - livingMafia;
  if (livingMafia === 0) return "town";
  if (livingMafia >= livingTown) return "mafia";
  return null;
}

function withWinner(state: MafiaState): MafiaState {
  const winner = winningFaction(state);
  if (!winner) return state;
  return {
    ...state,
    phase: "finished",
    winner,
    deadlineAt: null,
    scores: Object.fromEntries(
      state.players.map((playerId) => [
        playerId,
        winner === "mafia"
          ? Number(state.roles[playerId] === "mafia")
          : Number(state.roles[playerId] !== "mafia"),
      ]),
    ),
  };
}

function mostVotedTarget(
  votes: Array<string | null>,
  countAbstentions = false,
): { targetId: string | null; tied: boolean } {
  const counts = new Map<string | null, number>();
  for (const targetId of votes) {
    if (targetId || countAbstentions) counts.set(targetId, (counts.get(targetId) ?? 0) + 1);
  }
  const ordered = [...counts.entries()].sort(
    (left, right) =>
      right[1] - left[1] || String(left[0] ?? "").localeCompare(String(right[0] ?? "")),
  );
  if (!ordered[0]) return { targetId: null, tied: false };
  if (ordered[1]?.[1] === ordered[0][1]) return { targetId: null, tied: true };
  return { targetId: ordered[0][0], tied: false };
}

function beginNight(state: MafiaState, now: number): MafiaState {
  return {
    ...state,
    phase: "night",
    mafiaVotes: {},
    doctorTargetId: null,
    detectiveTargetId: null,
    readyToVoteIds: [],
    votes: {},
    nightVictimId: null,
    nightSaved: false,
    lastEliminatedId: null,
    deadlineAt: now + state.nightSeconds * 1_000,
  };
}

function requiredNightActionsComplete(state: MafiaState): boolean {
  const mafiaReady = livingWithRole(state, "mafia").every((id) => Boolean(state.mafiaVotes[id]));
  const doctorReady = livingWithRole(state, "doctor").length === 0 || Boolean(state.doctorTargetId);
  const detectiveReady =
    livingWithRole(state, "detective").length === 0 || Boolean(state.detectiveTargetId);
  return mafiaReady && doctorReady && detectiveReady;
}

function resolveNight(state: MafiaState, now: number): MafiaState {
  // A missing Mafia action is an abstention at the deadline. One submitted vote
  // must not decide the whole faction's kill while teammates are inactive.
  const mafiaChoice = mostVotedTarget(
    livingWithRole(state, "mafia").map((id) => state.mafiaVotes[id] ?? null),
    true,
  );
  const attackedId = mafiaChoice.targetId;
  const saved = Boolean(attackedId && attackedId === state.doctorTargetId);
  const victimId = saved ? null : attackedId;
  const aliveIds = victimId ? state.aliveIds.filter((id) => id !== victimId) : state.aliveIds;
  const investigations = Object.fromEntries(
    Object.entries(state.investigations).map(([id, entries]) => [id, [...entries]]),
  );
  for (const detectiveId of livingWithRole(state, "detective")) {
    if (!state.detectiveTargetId) continue;
    investigations[detectiveId] = [
      ...(investigations[detectiveId] ?? []),
      {
        day: state.day,
        targetId: state.detectiveTargetId,
        isMafia: state.roles[state.detectiveTargetId] === "mafia",
      },
    ].slice(-20);
  }
  const next = withWinner({
    ...state,
    aliveIds,
    phase: "dawn",
    investigations,
    nightVictimId: victimId,
    nightSaved: saved,
    lastProtectedId: state.doctorTargetId,
    lastEliminatedId: victimId,
    history: [
      ...state.history,
      {
        day: state.day,
        kind: "night" as const,
        eliminatedId: victimId,
        saved,
        tied: mafiaChoice.tied,
      },
    ].slice(-40),
    // Keep the dawn report readable without stalling the next discussion.
    deadlineAt: now + 4_000,
  });
  return next;
}

function beginVoting(state: MafiaState, now: number): MafiaState {
  return {
    ...state,
    phase: "voting",
    votes: {},
    deadlineAt: now + state.votingSeconds * 1_000,
  };
}

function resolveVoting(state: MafiaState, now: number): MafiaState {
  // Explicit abstentions and ballots missing at the deadline participate in the
  // result. If abstain wins, or shares the lead, nobody is eliminated.
  const decision = mostVotedTarget(
    state.aliveIds.map((id) => (id in state.votes ? (state.votes[id] ?? null) : null)),
    true,
  );
  const eliminatedId = decision.targetId;
  const aliveIds = eliminatedId
    ? state.aliveIds.filter((playerId) => playerId !== eliminatedId)
    : state.aliveIds;
  const resolved = withWinner({
    ...state,
    aliveIds,
    lastEliminatedId: eliminatedId,
    history: [
      ...state.history,
      {
        day: state.day,
        kind: "vote" as const,
        eliminatedId,
        saved: false,
        tied: decision.tied,
      },
    ].slice(-40),
  });
  return resolved.phase === "finished"
    ? resolved
    : beginNight({ ...resolved, day: state.day + 1 }, now);
}

function settleAfterRosterChange(state: MafiaState, now: number): MafiaState {
  const concluded = withWinner(state);
  if (concluded.phase === "finished") return concluded;
  if (
    concluded.phase === "role_reveal" &&
    concluded.players.every((id) => concluded.roleAcknowledgedIds.includes(id))
  )
    return beginNight(concluded, now);
  if (concluded.phase === "night" && requiredNightActionsComplete(concluded))
    return resolveNight(concluded, now);
  if (
    concluded.phase === "discussion" &&
    concluded.aliveIds.every((id) => concluded.readyToVoteIds.includes(id))
  )
    return beginVoting(concluded, now);
  if (concluded.phase === "voting" && concluded.aliveIds.every((id) => id in concluded.votes))
    return resolveVoting(concluded, now);
  return concluded;
}

function publicState(state: MafiaState, viewer?: ViewerContext): MafiaPublicState {
  const viewerId = viewer?.viewerId ?? "";
  const ownRole = state.roles[viewerId] ?? null;
  const finished = state.phase === "finished";
  const revealedRoles = Object.fromEntries(
    state.players
      .filter(
        (playerId) => finished || (state.revealRolesOnDeath && !state.aliveIds.includes(playerId)),
      )
      .map((playerId) => [playerId, state.roles[playerId] as MafiaRole]),
  );
  const ownNightTargetId =
    ownRole === "mafia"
      ? (state.mafiaVotes[viewerId] ?? null)
      : ownRole === "doctor"
        ? state.doctorTargetId
        : ownRole === "detective"
          ? state.detectiveTargetId
          : null;
  return {
    players: [...state.players],
    aliveIds: [...state.aliveIds],
    phase: state.phase,
    day: state.day,
    roleAcknowledgedIds: [...state.roleAcknowledgedIds],
    readyToVoteIds: [...state.readyToVoteIds],
    votes: state.phase === "voting" || finished ? { ...state.votes } : {},
    nightVictimId: state.nightVictimId,
    nightSaved: state.nightSaved,
    lastEliminatedId: state.lastEliminatedId,
    history: state.history.map((entry) => ({ ...entry })),
    deadlineAt: state.deadlineAt,
    winner: state.winner,
    scores: { ...state.scores },
    ownRole,
    mafiaTeamIds:
      ownRole === "mafia" || finished
        ? state.players.filter((playerId) => state.roles[playerId] === "mafia")
        : [],
    revealedRoles,
    ownNightTargetId,
    mafiaNightVotes: ownRole === "mafia" ? { ...state.mafiaVotes } : {},
    investigations: ownRole === "detective" ? [...(state.investigations[viewerId] ?? [])] : [],
  };
}

export const mafiaGame: TopzleGameDefinition<
  MafiaState,
  MafiaEvent,
  MafiaSettings,
  {
    role: MafiaRole;
    mafiaTeamIds: string[];
    investigations: Array<z.infer<typeof investigationSchema>>;
  }
> = {
  id: "mafia",
  version: "1.1.0",
  name: "Mafia",
  shortDescription: "Read the room, protect the town, and expose the hidden Mafia.",
  longDescription:
    "A server-moderated social deduction game with secret roles, private night actions, live discussion, public voting, and cinematic day-and-night rounds.",
  category: "social",
  icon: "hat-glasses",
  cover: "mafia",
  minPlayers: 4,
  maxPlayers: 12,
  supportsGuests: true,
  supportsLocalPlay: false,
  supportsOnlinePlay: true,
  supportsSpectators: true,
  supportsTeams: true,
  supportsAiPlayers: false,
  supportsLocalAi: false,
  supportsCloudAi: false,
  supportsTournaments: false,
  supportsPersistence: true,
  requiredCapabilities: ["text", "audio"],
  supportedLayouts: ["game_fullscreen", "floating", "avatar_strip", "picture_in_picture"],
  preferredLayout: "game_fullscreen",
  stateSchema: mafiaStateSchema,
  eventSchema: mafiaEventSchema,
  settingsSchema: mafiaSettingsSchema,
  createInitialState(context, settings) {
    const players = [...new Set(context.playerIds)].slice(0, 12);
    if (players.length < 4) throw new Error("Mafia needs at least four unique players");
    const counts = mafiaRoleCounts(players.length);
    const roleDeck: MafiaRole[] = [
      ...Array.from({ length: counts.mafia }, () => "mafia" as const),
      "detective",
      "doctor",
      ...Array.from({ length: counts.citizen }, () => "citizen" as const),
    ];
    const dealt = shuffled(roleDeck, context);
    return {
      players,
      roles: Object.fromEntries(
        players.map((playerId, index) => [playerId, dealt[index] ?? "citizen"]),
      ),
      aliveIds: [...players],
      phase: "role_reveal",
      day: 1,
      roleAcknowledgedIds: [],
      mafiaVotes: {},
      doctorTargetId: null,
      detectiveTargetId: null,
      lastProtectedId: null,
      investigations: {},
      readyToVoteIds: [],
      votes: {},
      nightVictimId: null,
      nightSaved: false,
      lastEliminatedId: null,
      history: [],
      deadlineAt: context.now + 30_000,
      winner: null,
      revealRolesOnDeath: settings.revealRolesOnDeath,
      discussionSeconds: settings.discussionSeconds,
      votingSeconds: settings.votingSeconds,
      nightSeconds: settings.nightSeconds,
      scores: Object.fromEntries(players.map((playerId) => [playerId, 0])),
    };
  },
  validateEvent(state, event, context) {
    const actorId = context.actorId ?? "";
    if (!state.players.includes(actorId))
      return { valid: false, code: "spectator", message: "Spectators can watch this mystery." };
    if (!state.aliveIds.includes(actorId) && event.type !== "acknowledge_role")
      return { valid: false, code: "eliminated", message: "Eliminated players may spectate." };
    if (event.type === "acknowledge_role")
      return {
        valid: state.phase === "role_reveal" && !state.roleAcknowledgedIds.includes(actorId),
        code: "role_already_seen",
        message: "Your role is already locked in.",
      };
    if (event.type === "advance_phase")
      return {
        valid: state.phase === "dawn",
        code: "not_dawn",
        message: "The dawn report is not ready to advance.",
      };
    if (event.type === "night_action") {
      const role = state.roles[actorId];
      if (!role)
        return { valid: false, code: "missing_role", message: "Your role could not be found." };
      if (state.phase !== "night")
        return { valid: false, code: "not_night", message: "Night actions are closed." };
      if (!(["mafia", "doctor", "detective"] as MafiaRole[]).includes(role))
        return { valid: false, code: "no_night_power", message: "Your role sleeps tonight." };
      if (!state.aliveIds.includes(event.targetId))
        return { valid: false, code: "invalid_target", message: "Choose a living player." };
      if (role !== "doctor" && event.targetId === actorId)
        return { valid: false, code: "self_target", message: "Choose another player." };
      if (role === "mafia" && state.roles[event.targetId] === "mafia")
        return {
          valid: false,
          code: "mafia_target",
          message: "The Mafia cannot target a teammate.",
        };
      return { valid: true };
    }
    if (event.type === "ready_to_vote")
      return {
        valid: state.phase === "discussion" && !state.readyToVoteIds.includes(actorId),
        code: "not_discussing",
        message: "Voting is not ready yet.",
      };
    if (state.phase !== "voting")
      return { valid: false, code: "not_voting", message: "The town is not voting now." };
    if (actorId in state.votes)
      return { valid: false, code: "already_voted", message: "Your vote is locked." };
    if (event.targetId && !state.aliveIds.includes(event.targetId))
      return { valid: false, code: "invalid_target", message: "Choose a living player." };
    if (event.targetId === actorId)
      return { valid: false, code: "self_vote", message: "You cannot vote for yourself." };
    return { valid: true };
  },
  reduceEvent(state, event, context) {
    const actorId = context.actorId ?? "";
    if (event.type === "acknowledge_role") {
      const roleAcknowledgedIds = [...new Set([...state.roleAcknowledgedIds, actorId])];
      const next = { ...state, roleAcknowledgedIds };
      return roleAcknowledgedIds.length === state.players.length
        ? beginNight(next, context.now)
        : next;
    }
    if (event.type === "advance_phase")
      return {
        ...state,
        phase: "discussion",
        readyToVoteIds: [],
        deadlineAt: context.now + state.discussionSeconds * 1_000,
      };
    if (event.type === "night_action") {
      const role = state.roles[actorId];
      const next =
        role === "mafia"
          ? { ...state, mafiaVotes: { ...state.mafiaVotes, [actorId]: event.targetId } }
          : role === "doctor"
            ? { ...state, doctorTargetId: event.targetId }
            : { ...state, detectiveTargetId: event.targetId };
      return requiredNightActionsComplete(next) ? resolveNight(next, context.now) : next;
    }
    if (event.type === "ready_to_vote") {
      const readyToVoteIds = [...new Set([...state.readyToVoteIds, actorId])];
      const next = { ...state, readyToVoteIds };
      return readyToVoteIds.length === state.aliveIds.length
        ? beginVoting(next, context.now)
        : next;
    }
    const votes = { ...state.votes, [actorId]: event.targetId };
    const next = { ...state, votes };
    return Object.keys(votes).length === state.aliveIds.length
      ? resolveVoting(next, context.now)
      : next;
  },
  getPublicState: (state, viewer) => publicState(state, viewer),
  getPrivatePlayerState(state, playerId) {
    const role = state.roles[playerId] ?? "citizen";
    return {
      role,
      mafiaTeamIds:
        role === "mafia"
          ? state.players.filter((candidate) => state.roles[candidate] === "mafia")
          : [],
      investigations: state.investigations[playerId] ?? [],
    };
  },
  getSpectatorState: (state) => publicState(state),
  getAvailableActions(state, playerId) {
    if (!state.players.includes(playerId)) return [];
    if (state.phase === "role_reveal" && !state.roleAcknowledgedIds.includes(playerId))
      return [{ type: "acknowledge_role" }];
    if (!state.aliveIds.includes(playerId)) return [];
    if (state.phase === "dawn") return [{ type: "advance_phase" }];
    const role = state.roles[playerId];
    if (state.phase === "night" && role !== "citizen")
      return state.aliveIds
        .filter(
          (targetId) =>
            (role === "doctor" || targetId !== playerId) &&
            (role !== "mafia" || state.roles[targetId] !== "mafia"),
        )
        .map((targetId) => ({ type: "night_action", targetId }));
    if (state.phase === "discussion" && !state.readyToVoteIds.includes(playerId))
      return [{ type: "ready_to_vote" }];
    if (state.phase === "voting" && !(playerId in state.votes))
      return [
        ...state.aliveIds
          .filter((targetId) => targetId !== playerId)
          .map((targetId) => ({ type: "cast_vote", targetId })),
        { type: "cast_vote", targetId: null },
      ];
    return [];
  },
  calculateResult(state): GameResult | null {
    if (state.phase !== "finished" || !state.winner) return null;
    return {
      status: "completed",
      winnerIds: state.players.filter((playerId) => state.scores[playerId] === 1),
      scores: state.scores,
      reason: state.winner === "town" ? "mafia_exposed" : "mafia_took_control",
    };
  },
  getNextDeadline: (state) => state.deadlineAt,
  handleDeadline(state, context) {
    if (!state.deadlineAt || state.deadlineAt > context.now || state.phase === "finished")
      return state;
    if (state.phase === "role_reveal") return beginNight(state, context.now);
    if (state.phase === "night") return resolveNight(state, context.now);
    if (state.phase === "dawn")
      return {
        ...state,
        phase: "discussion",
        readyToVoteIds: [],
        deadlineAt: context.now + state.discussionSeconds * 1_000,
      };
    if (state.phase === "discussion") return beginVoting(state, context.now);
    if (state.phase === "voting") return resolveVoting(state, context.now);
    return state;
  },
  handlePlayerDeparture(state, departure, context) {
    const from = departure.playerId;
    const to = departure.replacementPlayerId;
    if (!state.players.includes(from)) return null;
    const replaceId = (value: string) => (value === from && to ? to : value);
    const replaceTarget = (value: string | null) => (value === from ? (to ?? null) : value);
    const remapRecord = <T>(record: Record<string, T>): Record<string, T> =>
      Object.fromEntries(
        Object.entries(record)
          .filter(([key]) => key !== from || Boolean(to))
          .map(([key, value]) => [replaceId(key), value]),
      );
    const roles = remapRecord(state.roles);
    const scores = remapRecord(state.scores);
    const investigations = Object.fromEntries(
      Object.entries(remapRecord(state.investigations)).map(([key, entries]) => [
        key,
        entries.map((entry) => ({ ...entry, targetId: replaceId(entry.targetId) })),
      ]),
    );
    const players = to ? state.players.map(replaceId) : state.players.filter((id) => id !== from);
    const aliveIds = to
      ? state.aliveIds.map(replaceId)
      : state.aliveIds.filter((id) => id !== from);
    const next: MafiaState = {
      ...state,
      players,
      aliveIds,
      roles,
      scores,
      investigations,
      roleAcknowledgedIds: to
        ? state.roleAcknowledgedIds.map(replaceId)
        : state.roleAcknowledgedIds.filter((id) => id !== from),
      mafiaVotes: Object.fromEntries(
        Object.entries(remapRecord(state.mafiaVotes))
          .map(([id, targetId]) => [id, replaceTarget(targetId)] as const)
          .filter((entry): entry is readonly [string, string] => entry[1] !== null),
      ),
      doctorTargetId:
        !to && state.roles[from] === "doctor" ? null : replaceTarget(state.doctorTargetId),
      detectiveTargetId:
        !to && state.roles[from] === "detective" ? null : replaceTarget(state.detectiveTargetId),
      lastProtectedId: replaceTarget(state.lastProtectedId),
      readyToVoteIds: to
        ? state.readyToVoteIds.map(replaceId)
        : state.readyToVoteIds.filter((id) => id !== from),
      votes: Object.fromEntries(
        Object.entries(remapRecord(state.votes))
          .filter(([, targetId]) => targetId !== from || Boolean(to))
          .map(([id, targetId]) => [id, targetId ? replaceId(targetId) : null]),
      ),
      nightVictimId: state.nightVictimId ? replaceId(state.nightVictimId) : null,
      lastEliminatedId: state.lastEliminatedId ? replaceId(state.lastEliminatedId) : null,
      history: state.history.map((entry) => ({
        ...entry,
        eliminatedId: entry.eliminatedId ? replaceId(entry.eliminatedId) : null,
      })),
    };
    return {
      state: settleAfterRosterChange(next, context.now),
      ...(to ? { replacementPlayerId: to } : {}),
    };
  },
};
